import { useState } from "react";
import { isAxiosError } from "axios";
import { format, subYears } from "date-fns";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toExportCsvRequest } from "@/components/itsm-import/export-csv";
import {
  getAvailableProviders,
  useCreateImportJob,
  useTenantCredentials,
} from "@/hooks/use-itsm-import";
import { IMPORT_PROVIDERS } from "@/lib/types/itsm-import";
import type { CreateImportJobRequest } from "@/lib/types/itsm-import";
import { itsmDisplayName } from "@/lib/solution-engineer/ticket-report-prompt";
import { ClaudeReportHandoff } from "./claude-report-handoff";
import { ToolCard } from "./tool-card";

const providerLabel = (provider: string) =>
  IMPORT_PROVIDERS.find((p) => p.value === provider)?.label ?? provider;

/** FastAPI answers with a string, or with a list of validation errors. */
function errorDetail(error: unknown): string | undefined {
  if (!isAxiosError<{ detail?: unknown }>(error)) return undefined;
  const detail = error.response?.data.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const first: unknown = detail[0];
    if (first && typeof first === "object" && "msg" in first) {
      return String(first.msg).split(/\r?\n/)[0];
    }
  }
  return undefined;
}

export function ExportTicketsTool({ tenantId }: { tenantId: string }) {
  const { data: credentials, isLoading, isError } = useTenantCredentials(tenantId);
  const createJob = useCreateImportJob(tenantId);
  const [credentialId, setCredentialId] = useState("");
  const [board, setBoard] = useState("");
  const [job, setJob] = useState<{ id: string; itsmName: string } | null>(null);

  const credential = credentials?.find((c) => c.id === credentialId);
  // A credential maps to exactly one import provider
  const provider = credential ? getAvailableProviders([credential])[0] : undefined;
  const needsBoard = provider === "monday";

  const onExport = () => {
    if (!credential || !provider) return;
    const request: CreateImportJobRequest = {
      source: "provider",
      provider,
      credential_id: credential.id,
      since_date: format(subYears(new Date(), 1), "yyyy-MM-dd"),
      include_closed: true,
      source_project: needsBoard ? board.trim() : undefined,
      // Freshservice is the only provider that also returns changes
      record_types: provider === "freshservice" ? ["ticket"] : undefined,
    };
    createJob.mutate(toExportCsvRequest(request), {
      onSuccess: (created) => {
        setJob({ id: created.id, itsmName: itsmDisplayName(providerLabel(provider)) });
        toast.success("CSV export job created");
      },
      onError: (err) => {
        toast.error(errorDetail(err) ?? "Failed to create the export job");
      },
    });
  };

  return (
    <ToolCard
      icon={FileSpreadsheet}
      title="Export last year's tickets"
      description="Pulls the last 12 months of tickets, open and closed, from the tenant's connected ITSM into one CSV file. Nothing is created in Harmony."
    >
      {isError ? (
        <p className="text-destructive text-sm">Failed to load the tenant&apos;s ITSM integrations.</p>
      ) : !isLoading && credentials?.length === 0 ? (
        <p className="text-muted-foreground text-sm">This tenant has no connected ITSM.</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label>ITSM integration</Label>
              <Select
                value={credentialId}
                onValueChange={(value) => {
                  setCredentialId(value);
                  setJob(null);
                }}
                disabled={isLoading}
              >
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder={isLoading ? "Loading..." : "Select an integration"} />
                </SelectTrigger>
                <SelectContent>
                  {credentials?.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.label || c.name} · {providerLabel(getAvailableProviders([c])[0] ?? c.provider)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {needsBoard && (
              <div className="space-y-1.5">
                <Label htmlFor="export-board">monday board ID</Label>
                <Input
                  id="export-board"
                  value={board}
                  onChange={(e) => { setBoard(e.target.value); }}
                  className="w-[180px]"
                />
              </div>
            )}
            <Button
              size="sm"
              onClick={onExport}
              disabled={!provider || (needsBoard && !board.trim()) || createJob.isPending}
            >
              {createJob.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Export to CSV
            </Button>
          </div>
          {job && (
            <ClaudeReportHandoff
              key={job.id}
              tenantId={tenantId}
              jobId={job.id}
              itsmName={job.itsmName}
            />
          )}
        </div>
      )}
    </ToolCard>
  );
}
